import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Save, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { apiUtils } from '../utils/api';

const CategoryModal = ({ 
  isOpen, 
  onClose, 
  categoryId = null, 
  onSuccess 
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const isEditing = Boolean(categoryId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      isActive: true
    }
  });

  const watchName = watch('name');

  // Fetch category data when editing
  useEffect(() => {
    if (isOpen && isEditing) {
      fetchCategory();
    } else if (isOpen) {
      reset({
        name: '',
        description: '',
        isActive: true
      });
    }
  }, [isOpen, isEditing, categoryId]);

  const fetchCategory = async () => {
    setLoading(true);
    const response = await apiUtils.fetchOne(`/categories/${categoryId}`);
    
    if (response.success) {
      const category = response.data;
      reset({
        name: category.name,
        description: category.description || '',
        isActive: category.isActive
      });
    } else {
      toast.error('Failed to fetch category details');
      onClose();
    }
    setLoading(false);
  };

  const onSubmit = async (data) => {
    setSubmitting(true);

    try {
      const endpoint = isEditing ? `/categories/${categoryId}` : '/categories';
      const method = isEditing ? 'update' : 'create';
      
      const response = await apiUtils[method](endpoint, data);

      if (response.success) {
        toast.success(`Category ${isEditing ? 'updated' : 'created'} successfully`);
        onSuccess();
        onClose();
        reset();
      } else {
        toast.error(response.error || `Failed to ${isEditing ? 'update' : 'create'} category`);
      }
    } catch (error) {
      console.error('Category modal error:', error);
      toast.error('An unexpected error occurred');
    }

    setSubmitting(false);
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
      reset();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center">
          <div className="p-2 bg-brand-100 rounded-lg mr-3">
            <FolderOpen className="h-5 w-5 text-brand-600" />
          </div>
          {isEditing ? 'Edit Category' : 'Add New Category'}
        </div>
      }
      size="md"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" text="Loading category..." />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Name *
            </label>
            <input
              {...register('name', { 
                required: 'Category name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
                maxLength: { value: 50, message: 'Name cannot exceed 50 characters' }
              })}
              type="text"
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${
                errors.name ? 'border-red-300 focus:ring-red-500' : ''
              }`}
              placeholder="e.g., Citrus Fruits"
            />
            {errors.name && <p className="text-red-500 text-sm mt-2">{errors.name.message}</p>}
            
            {/* Show generated slug preview */}
            {watchName && (
              <p className="text-xs text-gray-500 mt-2">
                URL slug: {watchName.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              {...register('description', {
                maxLength: { value: 500, message: 'Description cannot exceed 500 characters' }
              })}
              rows={3}
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 resize-none ${
                errors.description ? 'border-red-300 focus:ring-red-500' : ''
              }`}
              placeholder="Describe this category of products..."
            />
            {errors.description && <p className="text-red-500 text-sm mt-2">{errors.description.message}</p>}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div>
              <label className="text-sm font-medium text-gray-700">Active Status</label>
              <p className="text-xs text-gray-500">
                Inactive categories won't be visible
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                {...register('isActive')}
                type="checkbox"
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500"></div>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-6">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-brand-500 to-brand-600 text-white py-3 px-6 rounded-xl font-medium hover:from-brand-600 hover:to-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
            >
              {submitting ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEditing ? 'Update Category' : 'Create Category'}
            </button>

            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex-1 sm:flex-none sm:px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default CategoryModal;